/** App-wide React context: services (data), auth, toasts and the modal (ModalProvider sits inside the router, so modals can navigate). */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { Permission, Role, User } from '@/types/models';
import type { AuthAdapter } from '@/auth/authAdapter';
import { can as canDo } from '@/auth/permissions';
import type { Services } from '@/services/services';

/* ---------------- services ---------------- */

const ServicesCtx = createContext<Services | null>(null);
export const useServices = (): Services => {
  const s = useContext(ServicesCtx);
  if (!s) throw new Error('useServices must be used inside <AppProviders>.');
  return s;
};

/* ---------------- auth ---------------- */

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string;
  adapter: AuthAdapter;
  can: (perm: Permission) => boolean;
  logIn: () => Promise<void>;
  logOut: () => Promise<void>;
  setDemoRole?: (role: Role) => Promise<void>;
}
const AuthCtx = createContext<AuthState | null>(null);
export const useAuth = (): AuthState => {
  const a = useContext(AuthCtx);
  if (!a) throw new Error('useAuth must be used inside <AppProviders>.');
  return a;
};

function AuthProvider({ adapter, children }: { adapter: AuthAdapter; children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setUser(await adapter.getCurrentUser());
    } catch (e) {
      setUser(null);
      setError(e instanceof Error ? e.message : 'Could not check who is signed in.');
    } finally {
      setLoading(false);
    }
  }, [adapter]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      error,
      adapter,
      can: (perm) => canDo(user, perm),
      logIn: async () => {
        await adapter.logIn();
        await refresh();
      },
      logOut: async () => {
        await adapter.logOut();
        await refresh();
      },
      setDemoRole: adapter.setDemoRole
        ? async (role) => {
            setUser(await adapter.setDemoRole!(role));
          }
        : undefined,
    }),
    [user, loading, error, adapter, refresh],
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

/* ---------------- toasts ---------------- */

interface Toast {
  id: number;
  msg: string;
  err: boolean;
}
type ToastFn = (msg: string, err?: boolean) => void;
const ToastCtx = createContext<ToastFn>(() => {});
export const useToast = (): ToastFn => useContext(ToastCtx);

function ToastProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<Toast[]>([]);
  const seq = useRef(0);
  const remove = (id: number) => setList((l) => l.filter((t) => t.id !== id));
  const toast = useCallback<ToastFn>((msg, err = false) => {
    const id = ++seq.current;
    setList((l) => [...l, { id, msg, err }]);
    setTimeout(() => remove(id), 3500);
  }, []);
  return (
    <ToastCtx.Provider value={toast}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {list.map((t) => (
          <div key={t.id} className={'toast' + (t.err ? ' err' : '')}>
            <span>{t.msg}</span>
            <button aria-label="Close" onClick={() => remove(t.id)}>
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/* ---------------- modal (one at a time, like the original) ---------------- */

export type ModalRender = (close: () => void) => ReactNode;
interface ModalApi {
  open: (render: ModalRender, opts?: { wide?: boolean }) => void;
  close: () => void;
}
const ModalCtx = createContext<ModalApi | null>(null);
export const useModal = (): ModalApi => {
  const m = useContext(ModalCtx);
  if (!m) throw new Error('useModal must be used inside <AppProviders>.');
  return m;
};

export function ModalProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ render: ModalRender; wide: boolean; key: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const seq = useRef(0);
  const close = useCallback(() => setState(null), []);
  const open = useCallback<ModalApi['open']>((render, opts) => setState({ render, wide: !!opts?.wide, key: ++seq.current }), []);
  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => {
      const f = box.current?.querySelector<HTMLElement>('input:not([disabled]):not([readonly]),select:not([disabled]),textarea:not([disabled])');
      f?.focus();
    }, 30);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [state, close]);
  const api = useMemo(() => ({ open, close }), [open, close]);
  return (
    <ModalCtx.Provider value={api}>
      {children}
      <div
        className={'overlay' + (state ? ' open' : '')}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) close();
        }}
      >
        {state && (
          <div className={'modal' + (state.wide ? ' wide' : '')} role="dialog" aria-modal="true" ref={box} key={state.key}>
            {state.render(close)}
          </div>
        )}
      </div>
    </ModalCtx.Provider>
  );
}

/* ---------------- root ---------------- */

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
      mutations: { retry: 0 },
    },
  });
}

export function AppProviders({ services, auth, queryClient, children }: { services: Services; auth: AuthAdapter; queryClient: QueryClient; children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ServicesCtx.Provider value={services}>
        <AuthProvider adapter={auth}>
          <ToastProvider>{children}</ToastProvider>
        </AuthProvider>
      </ServicesCtx.Provider>
    </QueryClientProvider>
  );
}
